import {
  enableProdMode,
  importProvidersFrom,
  provideZonelessChangeDetection,
} from '@angular/core';
import {bootstrapApplication} from '@angular/platform-browser';
import {provideAnimations} from '@angular/platform-browser/animations';
import {
  provideRouter,
  RouteReuseStrategy,
  withInMemoryScrolling,
} from '@angular/router';
import {provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {APP_BASE_HREF} from '@angular/common';
import {
  OverlayContainer,
  FullscreenOverlayContainer,
} from '@angular/cdk/overlay';
import {UrlHandlingStrategy} from '@angular/router';

import {HttpClient} from '@angular/common/http';
import {TranslateModule, TranslateLoader} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';

import {MatIconModule} from '@angular/material/icon';
import {MatDialogModule} from '@angular/material/dialog';

import {Amplify, ResourcesConfig} from 'aws-amplify';

import {AppComponent} from './app/app.component';
import {AppRouteReuseStrategy} from './app/app-route-reuse-strategy';
import {AppUrlHandlingStrategy} from './app/app-url-handling-strategy';
import {routes} from './app/app.routes';
import {environment} from './environments/environment';

import {APIService} from '@services/api.service';
import {BluetoothConnectionService} from '@services/bluetooth-connection.service';
import {CacheExpirationStrategyService} from '@services/cache-expiration-strategy.service';
import {CSVSerializerService} from '@services/csv-serializer.service';
import {DefaultCacheExpirationStrategyService} from '@services/default-cache-expiration-strategy.service';
import {ErrorReportReactor} from './app/reactors/error-report-api.reactor';
import {FileService} from '@services/file.service';
import {HttpService} from '@services/http.service';
import {S3Service} from '@services/s3.service';
import {ServiceCommsWrapperService} from '@services/service-comms-wrapper.service';
import {StorageWrapperService} from '@services/storage-wrapper.service';

import {APIStateStore} from '@stores/api-state.store';
import {ErrorReportStore} from '@stores/error-report.store';
import {GeolocationStore} from '@stores/geolocation.store';
import {MissingTransitionStrategy} from '@stores/entry-store-automaton/modification-strategy';

import {ExpireEntriesAction} from '@actions/expire-entries.action';
import {DispatcherModule} from '@services/websocket-message-processor/dispatcher.module';
import {webSocket} from 'rxjs/webSocket';
import {ActAsTeacherPolicyService} from './app/policies/act-as-teacher-policy.service';
import {LoaderStore} from '@stores/loader.store';
import {
  EventAPIServiceProxyHandler,
  EventAPIService,
  EventAPIServiceToken,
} from '@services/event-api.service';
import {AdvanceEntryStateAroundEventDecorator} from '@services/service-decorator/advance-entry-state-around-event.decorator';
import {ChangeActiveServiceDecorator} from '@services/service-decorator/change-active-service.decorator';
import {CopyEventInterceptorDecorator} from '@services/service-decorator/copy-event-interceptor.decorator';
import {EventIdManagerDecorator} from '@services/service-decorator/event-id-manager.decorator';
import {EventMatchDecorator} from '@services/service-decorator/event-match.decorator';
import {PosthogSpyDecorator} from '@services/service-decorator/posthog-spy.decorator';
import {
  CopyPosthogEventBuilder,
  PosthogSpy,
  ReadPosthogEventBuilder,
} from './app/spies/posthog.spy';
import {SyncEnforcerDecorator} from '@services/service-decorator/sync-enforcer.decorator';
import {registerServiceWorker} from '@utils/register-service-worker';

import {Workbox} from 'workbox-window';
import {
  messageBucketConfigName,
  scormBucketConfigName,
  usEast1,
} from '@constants/app.config';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

if (environment.production) {
  enableProdMode();
}

document.addEventListener('DOMContentLoaded', () => {
  bootstrapApplication(AppComponent, {
    providers: [
      provideZonelessChangeDetection(),
      provideAnimations(),
      provideHttpClient(withInterceptorsFromDi()),
      provideRouter(
        routes,
        withInMemoryScrolling({
          anchorScrolling: 'enabled',
          scrollPositionRestoration: 'enabled',
        }),
      ),
      importProvidersFrom(
        TranslateModule.forRoot({
          defaultLanguage: 'en',
          useDefaultLang: true,
          loader: {
            provide: TranslateLoader,
            useFactory: createTranslateLoader,
            deps: [HttpClient],
          },
        }),
        MatIconModule,
        MatDialogModule,
        DispatcherModule,
      ),
      {provide: APP_BASE_HREF, useValue: '/'},
      {provide: OverlayContainer, useClass: FullscreenOverlayContainer},
      {provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy},
      {provide: UrlHandlingStrategy, useClass: AppUrlHandlingStrategy},
      {provide: 'WINDOW', useFactory: () => window},
      {provide: 'DOCUMENT', useFactory: () => document},
      {provide: 'SCREEN', useFactory: () => screen},
      {provide: 'STORAGE', useFactory: () => localStorage},
      {provide: 'SESSIONSTORAGE', useFactory: () => sessionStorage},
      APIService,
      BluetoothConnectionService,
      CSVSerializerService,
      ErrorReportReactor,
      FileService,
      HttpService,
      S3Service,
      ServiceCommsWrapperService,
      StorageWrapperService,
      APIStateStore,
      ErrorReportStore,
      GeolocationStore,
      ExpireEntriesAction,
      PosthogSpy,
      ReadPosthogEventBuilder,
      CopyPosthogEventBuilder,
      {
        provide: DefaultCacheExpirationStrategyService,
        useFactory: () =>
          new DefaultCacheExpirationStrategyService({
            maxErrors: 5,
            maxAge: 7889400,
            maxEntries: 10000,
          }),
        deps: [],
      },
      {
        provide: CacheExpirationStrategyService,
        useExisting: DefaultCacheExpirationStrategyService,
      },
      {
        provide: 'missingTransitionBuilder',
        useFactory: (errorReportStore) => (i, c) =>
          new MissingTransitionStrategy(c, i, errorReportStore),
        deps: [ErrorReportStore],
      },
      {provide: 'webSocketFactory', useValue: webSocket},
      {
        provide: 'actAsTeacher',
        useClass: ActAsTeacherPolicyService,
      },
      LoaderStore,
      EventAPIServiceProxyHandler,
      AdvanceEntryStateAroundEventDecorator,
      ChangeActiveServiceDecorator,
      CopyEventInterceptorDecorator,
      EventIdManagerDecorator,
      EventMatchDecorator,
      PosthogSpyDecorator,
      SyncEnforcerDecorator,
      {
        provide: EventAPIServiceToken,
        useFactory: (
          pH: EventAPIServiceProxyHandler,
          cp: CopyEventInterceptorDecorator,
          adv: AdvanceEntryStateAroundEventDecorator,
          ch: ChangeActiveServiceDecorator,
          id: EventIdManagerDecorator,
          em: EventMatchDecorator,
          spy: PosthogSpyDecorator,
          sync: SyncEnforcerDecorator,
        ) => {
          ch.setService(new Proxy({}, pH) as EventAPIService);
          id.setService(ch);
          em.setService(id);
          adv.setService(em);
          cp.setService(adv);
          spy.setService(cp);
          sync.setService(spy);
          return sync;
        },
        deps: [
          EventAPIServiceProxyHandler,
          CopyEventInterceptorDecorator,
          AdvanceEntryStateAroundEventDecorator,
          ChangeActiveServiceDecorator,
          EventIdManagerDecorator,
          EventMatchDecorator,
          PosthogSpyDecorator,
          SyncEnforcerDecorator,
        ],
      },
    ],
  })
    .then((appRef) => {
      startServiceWorker('sw', appRef.injector.get(APIStateStore));
    })
    .catch((err) => console.log(err));
});

export const amplifyConfig: ResourcesConfig = {
  Auth: {
    Cognito: {
      identityPoolId: environment.identityPoolId,
      userPoolId: environment.userPoolId,
      userPoolClientId: environment.userPoolWebClientId,
      allowGuestAccess: true,
      loginWith: {
        oauth: {
          domain: environment.oAuthDomain,
          scopes: [
            'email',
            'openid',
            'aws.cognito.signin.user.admin',
            'profile',
          ],
          redirectSignIn: [`${environment.host}/`],
          redirectSignOut: [`${environment.host}/`],
          responseType: 'code' as const,
        },
      },
    },
  },
  API: {
    REST: {
      [environment.entryAPIGatewayName]: {
        endpoint: environment.entryAPIGatewayUrl,
        region: usEast1,
      },
      [environment.oneRosterAPIGatewayName]: {
        endpoint: environment.oneRosterAPIGatewayUrl,
        region: usEast1,
      },
      [environment.opensearchAPIGatewayName]: {
        endpoint: environment.opensearchAPIGatewayUrl,
        region: usEast1,
      },
      [environment.userStatsAPIGatewayName]: {
        endpoint: environment.userStatsAPIGatewayUrl,
        region: usEast1,
      },
      [environment.authAPIGatewayName]: {
        endpoint: environment.authAPIGatewayUrl,
        region: usEast1,
      },
      [environment.errorReportAPIEndpointName]: {
        endpoint: environment.errorReportAPIEndpointUrl,
        region: usEast1,
      },
    },
  },
  Storage: {
    S3: {
      bucket: environment.imageBucket,
      region: usEast1,
      buckets: {
        [messageBucketConfigName]: {
          bucketName: environment.messageBucket,
          region: usEast1,
        },
        [scormBucketConfigName]: {
          bucketName: environment.scormBucket,
          region: usEast1,
        },
      },
    },
  },
};

Amplify.configure(amplifyConfig);

function startServiceWorker(swName: string, apiStateStore: APIStateStore) {
  if ('serviceWorker' in navigator) {
    registerServiceWorker({
      workbox: new Workbox(`/${swName}.js`),
      apiStateStore,
      document,
      storage: sessionStorage,
      reload: () => window.location.reload(),
    });
  }
}
